import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Select, TextInput } from 'components/Form'
import { createApiRecord, updateApiRecord } from 'services/IntegrationService'
import { useDispatch, useSelector } from 'react-redux'
import { getRecord } from './store/dataSlice'
import reducer from './store'
import { injectReducer } from 'store'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Success } from 'utils/alerts'
import { getIntegrationData } from 'store/data/dataSlice'
injectReducer('integration', reducer)

const validationSchema = Yup.object().shape({
    name: Yup.string().required('company name field is required'),
    company: Yup.string().required('company field is required'),
    // customer: Yup.string().required('customer field is required'),
    integration_type: Yup.string().required('integration type field is required'),
    description: Yup.string().nullable(),
})

const Create = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { companyData, customersData, integrationTypeData } = useSelector(state => state.listData)
    const { view, record, loading } = useSelector(state => state.integration.data)

    useEffect(() => {
        if (id) {
            fetchData(id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fetchData = id => dispatch(getRecord(id))

    const handleDiscard = () => navigate('/company-management/integration')

    const handleSubmit = data => {
        if (id) {
            updateApiRecord(id, data)
                .then(() => {
                    Success('Updated Successfully!')
                    handleDiscard()
                    dispatch(getIntegrationData())
                })
                .catch(error => console.log('Error: ', error))
        } else {
            data.customer.map((item, index) =>
                createApiRecord({ ...data, customer: item })
                    .then(() => {
                        if (index === data.customer.length - 1) {
                            Success('Created Successfully!')
                            handleDiscard()
                            dispatch(getIntegrationData())
                        }
                    })
                    .catch(error => console.log('Error: ', error))
            )
        }
    }

    if (loading) {
        return null
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Maestro Integration</h2>
            </div>
            <div className="flex flex-col items-center">
                <div className="w-full bg-white border rounded-sm border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">
                            {id ? 'Update Integration' : 'Add New Integration'}
                        </h3>
                    </div>
                    <div className="flex flex-col gap-3 p-6">
                        <Formik
                            initialValues={id ? record : view}
                            validationSchema={validationSchema}
                            onSubmit={values => {
                                handleSubmit(values)
                            }}
                        >
                            {({ values, touched, errors }) => (
                                <Form>
                                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-4">
                                        <Field name="company">
                                            {({ field, form }) => (
                                                <Select
                                                    id="company"
                                                    name="company"
                                                    label="Company"
                                                    placeholder="Company"
                                                    className="md:col-span-2"
                                                    value={values?.company}
                                                    options={companyData}
                                                    onChange={e => {
                                                        form.setFieldValue(field.name, e.target.value)
                                                        form.setFieldValue('customer', '')
                                                    }}
                                                    error={touched.company && errors.company}
                                                    message={errors.company}
                                                />
                                            )}
                                        </Field>
                                        {id ? (
                                            <Field name="customer">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="customer"
                                                        name="customer"
                                                        label="Customer"
                                                        placeholder="Customer"
                                                        className="md:col-span-2"
                                                        value={values?.customer}
                                                        options={customersData?.filter(
                                                            item => item?.company?.id?.toString() === values?.company?.toString()
                                                        )}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                        error={touched.customer && errors.customer}
                                                        message={errors.customer}
                                                    />
                                                )}
                                            </Field>
                                        ) : (
                                            <Field name="customer">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="customer"
                                                        name="customer"
                                                        label="Customer"
                                                        placeholder="Customer"
                                                        className="md:col-span-2"
                                                        value={values?.customer || []}
                                                        options={customersData?.filter(
                                                            item => item?.company?.id?.toString() === values?.company?.toString()
                                                        )}
                                                        onChange={e => form.setFieldValue(field.name, e)}
                                                        error={touched.customer && errors.customer}
                                                        message={errors.customer}
                                                        multiple={true}
                                                    />
                                                )}
                                            </Field>
                                        )}
                                        <Field name="integration_type">
                                            {({ field, form }) => (
                                                <Select
                                                    id="integration_type"
                                                    name="integration_type"
                                                    label="Integration Type"
                                                    placeholder="Integration Type"
                                                    className="md:col-span-2"
                                                    value={values?.integration_type}
                                                    options={integrationTypeData}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    error={touched.integration_type && errors.integration_type}
                                                    message={errors.integration_type}
                                                />
                                            )}
                                        </Field>
                                        <Field name="name">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Integration Name"
                                                    name="name"
                                                    error={errors.name && touched.name}
                                                    message={errors.name}
                                                    value={values?.name}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="description">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Description"
                                                    name="description"
                                                    error={errors.description && touched.description}
                                                    message={errors.description}
                                                    value={values?.description}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="host">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Host"
                                                    name="host"
                                                    error={errors.host && touched.host}
                                                    message={errors.host}
                                                    value={values?.config?.host}
                                                    onChange={e => form.setFieldValue(`config.${field.name}`, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="port">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Port"
                                                    name="port"
                                                    error={errors.port && touched.port}
                                                    message={errors.port}
                                                    value={values?.config?.port}
                                                    onChange={e => form.setFieldValue(`config.${field.name}`, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                    <div className="flex items-center justify-end gap-4 mt-3">
                                        <Button
                                            className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                            onClick={handleDiscard}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Create
